export type DocumentReviewStatus = 'pending' | 'accepted' | 'rejected';

export interface DateValue {
  day: string;
  month: string;
  year: string;
}

export type DocumentCategory =
  | 'passport'
  | 'residence'
  | 'criminal'
  | 'asylum'
  | 'employment'
  | 'family'
  | 'vulnerability'
  | 'other';

export interface RequiredDocument {
  id: string;
  name: string;
  description?: string;
  category: DocumentCategory;
  docType?: string;
  uploaded: boolean;
  uploadedAt?: string;
  uploadedFile?: string;
  reviewStatus: DocumentReviewStatus;
  rejectionReason?: string;
  viewed?: boolean;
  needsApostille?: boolean;
  needsSwornTranslation?: boolean;
  apostilleReceived?: boolean;
  swornTranslationReceived?: boolean;
}

export interface CriminalCertificate {
  id: string;
  country: string;
  uploaded: boolean;
  uploadedAt?: string;
  uploadedFile?: string;
  reviewStatus: DocumentReviewStatus;
  rejectionReason?: string;
  certRequestedNotReceived: boolean;
  requestProofFile?: string;
}

export const CRIMINAL_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Angola','Argentina','Armenia','Australia','Austria','Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Bolivia','Bosnia and Herzegovina','Brazil','Bulgaria','Burkina Faso','Cambodia','Cameroon','Canada','Chile','China','Colombia','Congo','Costa Rica','Croatia','Cuba','Czech Republic','Denmark','Dominican Republic','Ecuador','Egypt','El Salvador','Estonia','Ethiopia','Finland','France','Georgia','Germany','Ghana','Greece','Guatemala','Guinea','Haiti','Honduras','Hungary','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Ivory Coast','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kosovo','Kyrgyzstan','Laos','Latvia','Lebanon','Libya','Lithuania','Luxembourg','Madagascar','Malaysia','Mali','Mauritania','Mexico','Moldova','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Nepal','Netherlands','Nicaragua','Niger','Nigeria','North Macedonia','Norway','Pakistan','Palestine','Panama','Paraguay','Peru','Philippines','Poland','Portugal','Romania','Russia','Rwanda','Saudi Arabia','Senegal','Serbia','Sierra Leone','Slovakia','Slovenia','Somalia','South Africa','South Korea','South Sudan','Sudan','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Togo','Tunisia','Turkey','Turkmenistan','Uganda','Ukraine','United Kingdom','United States','Uruguay','Uzbekistan','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe',
];

export const RESIDENCY_DOC_TYPES = [
  { value: 'Padron', label: 'Padrón / Empadronamiento' },
  { value: 'Rental', label: 'Rental contract' },
  { value: 'Medical', label: 'Medical record' },
  { value: 'School', label: 'School enrollment' },
  { value: 'Bank', label: 'Bank statement' },
  { value: 'Utility', label: 'Utility bill' },
  { value: 'Payslip', label: 'Nómina' },
  { value: 'Transfer', label: 'Transfer receipt' },
  { value: 'Course', label: 'Training certificate' },
  { value: 'Transport', label: 'Transport ticket' },
  { value: 'Police', label: 'Police report' },
  { value: 'Official', label: 'Official letter' },
  { value: 'Other', label: 'Other' },
];

export const ASYLUM_DOC_TYPES = [
  { value: 'receipt', label: 'Resguardo' },
  { value: 'card', label: 'Applicant card' },
  { value: 'resolution', label: 'Admission resolution' },
  { value: 'other', label: 'Other' },
];

export const EMPLOYMENT_DOC_TYPES = [
  { value: 'contract', label: 'Work contract' },
  { value: 'payslip', label: 'Nómina' },
  { value: 'vidalaboral', label: 'Vida laboral' },
  { value: 'joboffer', label: 'Job offer' },
  { value: 'other', label: 'Other' },
];

export const FAMILY_DOC_TYPES = [
  { value: 'marriage', label: 'Marriage certificate' },
  { value: 'libro', label: 'Libro de familia' },
  { value: 'birth', label: 'Birth certificate' },
  { value: 'cohabitation', label: 'Cohabitation proof' },
  { value: 'school', label: 'School enrollment' },
  { value: 'other', label: 'Other' },
];

export const OTHER_DOC_TYPES = [
  { value: 'PowerOfAttorney', label: 'Power of attorney' },
  { value: 'LegalRepresentation', label: 'Legal representation' },
  { value: 'Correspondence', label: 'Correspondence' },
  { value: 'Receipt', label: 'Receipt' },
  { value: 'Translation', label: 'Sworn translation' },
  { value: 'Apostille', label: 'Apostille' },
  /** Free-text description required in UI before upload */
  { value: 'otros', label: 'Otros' },
];

export const DOC_CATEGORIES: Record<
  DocumentCategory,
  { label: string; icon: string; description: string; multiple: boolean; optional?: boolean }
> = {
  passport: { label: 'Identity', icon: 'ri-file-text-line', description: 'Full passport, all pages. Expired passports accepted.', multiple: false },
  residence: { label: 'Proof of residence', icon: 'ri-home-4-line', description: 'Documents showing continuous presence in Spain', multiple: true },
  criminal: { label: 'Criminal record', icon: 'ri-shield-check-line', description: 'One certificate per country of residence in the 5 years before entering Spain. Apostilled/legalized and translated. Valid 3 months.', multiple: true },
  asylum: { label: 'Asylum documentation', icon: 'ri-shield-user-line', description: 'Resguardo, applicant card, or admission resolution. Asylum must have been filed before 1 Jan 2026.', multiple: true },
  employment: { label: 'Employment', icon: 'ri-briefcase-line', description: 'Work contract, job offer, or prior employment documentation. For self-employed: activity description.', multiple: true },
  family: { label: 'Family documents', icon: 'ri-group-line', description: 'Proof of family living together in Spain.', multiple: true },
  vulnerability: { label: 'Vulnerability report', icon: 'ri-heart-pulse-line', description: 'Anexo II vulnerability report.', multiple: false },
  other: { label: 'Other', icon: 'ri-file-add-line', description: 'Additional supporting documents', multiple: true, optional: true },
};
